import React from 'react'

import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button/Button'

import NavigateBeforeRounded from '@mui/icons-material/NavigateBeforeRounded'
import NavigateNextRounded from '@mui/icons-material/NavigateNextRounded'
import TodayRounded from '@mui/icons-material/TodayRounded'

import { Box, IconButton, Theme, useMediaQuery } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { datesAreOnSameMonth } from '../../../../screens/App/FamilyMember/FamilyCalendar/utils/datesAreTheSame'
import { getAppTheme } from '../../../../shared/config/getAppTheme'

interface IProps {
  selectedDate?: number
  changeSelectedDate?: (date: number) => void
}

const FamilyCalendarNav: React.FC<IProps> = ({ ...props }) => {
  const theme: Theme = getAppTheme()
  const downXs = useMediaQuery(theme.breakpoints.down('xs'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))

  const useStyles = makeStyles((theme: Theme) => ({
    barContainer: {
      width: '100%',
      display: 'flex',
      position: 'relative',
      justifyContent: 'space-between',
    },
    dateNav: {
      position: 'absolute',
      left: downXs ? 0 : (downMd ? 'calc(50% - 120px)' : 'calc(50% - 70px)'),
      marginTop: theme.spacing(1.5),
    },
    tabsNav: {
      marginTop: theme.spacing(2),
      marginLeft: 'auto',
    },
    pagination: {
      margin: '0',
      display: 'flex',
      padding: 0,
      flexWrap: 'wrap',
      listStyle: 'none',
      alignItems: 'center',
    },
    navItem: {
      fontSize: '1rem',
      lineHeight: '17px',
    },
    navLink: {
      margin: '0 1px',
    },
    disabled: {
      opacity: 0.38,
      pointerEvents: 'none',
      cursor: 'default',
    },
    loader: {
      display: 'flex',
      alignItems: 'center',
      marginTop: theme.spacing(2),
      width: '30px',
    },
  }))

  const classes = useStyles()
  const { t } = useTranslation()

  const Months = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ]

  const selectedDateObj = props.selectedDate && new Date(props.selectedDate)
  const today = new Date(Date.now())

  const onNext = () => {
    if (selectedDateObj) return props.changeSelectedDate && props.changeSelectedDate(selectedDateObj.setMonth(selectedDateObj.getMonth() + 1))
  }

  const onPrevious = () => {
    if (selectedDateObj) return props.changeSelectedDate && props.changeSelectedDate(selectedDateObj.setMonth(selectedDateObj.getMonth() - 1))
  }

  const setToday = () => {
    props.changeSelectedDate && props.changeSelectedDate(Date.now())
  }

  const disabled = () => {
    if (selectedDateObj) return datesAreOnSameMonth(selectedDateObj, today)
  }

  return (
    <Box className={classes.barContainer} component='div'>
      <nav className={classes.dateNav}>
        <ul className={classes.pagination}>
          <li className={`${classes.navItem}`}>
            <IconButton
              className={`${classes.navLink}`}
              component='div'
              onClick={onPrevious}
            >
              <NavigateBeforeRounded />
            </IconButton>
          </li>
          <li className={`${classes.navItem}`}>
            {selectedDateObj && `${Months[selectedDateObj.getMonth()]} ${selectedDateObj.getUTCFullYear()}`}
          </li>
          <li className={`${classes.navItem}`}>
            <IconButton
              className={`${classes.navLink}`}
              component='div'
              onClick={onNext}
            >
              <NavigateNextRounded />
            </IconButton>
          </li>
        </ul>
      </nav>
      <Box className={classes.tabsNav} component='div'>
        <Button
          variant='text'
          size='small'
          sx={{
            textTransform: 'capitalize',
            '& .MuiButton-iconSizeSmall': { mr: '5px', '& .MuiSvgIcon-root': { fontSize: '24px' } },
          }}
          disabled={disabled()}
          startIcon={<TodayRounded />}
          onClick={setToday}
        >
          {t('coreCalendar:today')}
        </Button>
      </Box>
    </Box>
  )
}

export default FamilyCalendarNav
